import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  CircularProgress,
  Alert,
  Link,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { useQuery } from "react-query";
import { fetchConversationKnowledgeMaterials } from "../api/client";
import ConversationTypeAssistant from "./ConversationDetails/ConversationTypeAssistant";

const NAV_COLLAPSED_KEY = "xpro.tipoKnowledgeMaterials.navCollapsed";

function readNavCollapsed() {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function persistNavCollapsed(v) {
  try {
    window.localStorage.setItem(NAV_COLLAPSED_KEY, v ? "1" : "0");
  } catch {
    /* ignore */
  }
}

const kindLabel = (k) => {
  const s = String(k || "").toLowerCase();
  if (s === "pdf") return "PDF";
  if (s === "xlsx") return "Planilha";
  if (s === "docx") return "Word";
  if (s === "link") return "Link";
  return "Arquivo";
};

function MaterialViewport({ item }) {
  if (!item?.url) {
    return (
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          bgcolor: "action.hover",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Escolha um material à esquerda.
        </Typography>
      </Paper>
    );
  }

  const kind = String(item.kind || "link").toLowerCase();
  const url = item.url;
  const title = item.title || "Material";

  const stageSx = {
    flex: 1,
    minHeight: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 1,
    border: 1,
    borderColor: "divider",
    bgcolor: kind === "pdf" ? "grey.200" : "grey.100",
  };

  if (kind === "pdf") {
    return (
      <Box sx={{ ...stageSx, p: 0 }}>
        <Box component="iframe" title={title} src={url} sx={{ flex: 1, width: "100%", minHeight: 0, border: 0 }} />
      </Box>
    );
  }

  if (kind === "xlsx" || kind === "docx") {
    return (
      <Paper variant="outlined" sx={{ flex: 1, minHeight: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 3, gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {kindLabel(kind)} — abra em nova aba para visualizar
        </Typography>
        <Button component={Link} href={url} target="_blank" rel="noopener noreferrer" variant="contained" endIcon={<OpenInNewIcon />}>
          Abrir {item.title || "arquivo"}
        </Button>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ flex: 1, minHeight: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 3, gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {kindLabel(kind)}
      </Typography>
      <Button component={Link} href={url} target="_blank" rel="noopener noreferrer" variant="contained" endIcon={<OpenInNewIcon />}>
        Abrir em nova aba
      </Button>
    </Paper>
  );
}

/**
 * Popup de materiais das fontes de conhecimento (tipo/subtipos) — layout inspirado em Ajuda e Treinamento.
 */
export default function TypeKnowledgeMaterialsDialog({ open, onClose, conversationId }) {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("md"));
  const [mainTab, setMainTab] = useState(0);
  const [levelIdx, setLevelIdx] = useState(0);
  const [itemIdx, setItemIdx] = useState(0);
  const [navCollapsed, setNavCollapsed] = useState(() => readNavCollapsed());

  const { data, isLoading, isError, refetch } = useQuery(
    ["conversationKnowledgeMaterials", conversationId],
    () => fetchConversationKnowledgeMaterials(conversationId),
    { enabled: open && !!conversationId, staleTime: 60_000, refetchOnWindowFocus: false }
  );

  const levels = Array.isArray(data?.levels) ? data.levels : [];
  const typePath = Array.isArray(data?.typePath) ? data.typePath : [];
  const selectedLevel = levels[levelIdx] || null;
  const items = selectedLevel?.items || [];
  const selectedItem = items[itemIdx] || null;

  useEffect(() => {
    if (!open) return;
    setMainTab(0);
    setLevelIdx(0);
    setItemIdx(0);
  }, [open, conversationId]);

  useEffect(() => {
    setItemIdx(0);
  }, [levelIdx]);

  useEffect(() => {
    if (levelIdx >= levels.length && levels.length > 0) setLevelIdx(0);
  }, [levels.length, levelIdx]);

  const toggleNav = () => {
    setNavCollapsed((prev) => {
      const next = !prev;
      persistNavCollapsed(next);
      return next;
    });
  };

  const pathLabel = typePath.length ? typePath.join(" → ") : "";

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={isSmDown}
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: isSmDown ? "100%" : "min(920px, 96vh)",
          height: isSmDown ? "100%" : "min(920px, 96vh)",
          display: "flex",
          flexDirection: "column",
          m: isSmDown ? 0 : 0.5,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexShrink: 0,
          py: 0.75,
          px: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <MenuBookIcon color="primary" sx={{ fontSize: 22 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography component="span" variant="subtitle1" fontWeight={700}>
            Materiais do atendimento
          </Typography>
          {pathLabel && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {pathLabel}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }} />
        {mainTab === 0 && levels.length > 0 && (
          <Tooltip title={navCollapsed ? "Mostrar menu" : "Ocultar menu"}>
            <IconButton size="small" onClick={toggleNav} aria-label="Alternar menu">
              {navCollapsed ? <MenuOpenIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </DialogTitle>

      <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: "divider", px: 1.5 }}>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          variant="fullWidth"
          sx={{ minHeight: 40 }}
        >
          <Tab icon={<MenuBookIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Materiais" sx={{ minHeight: 40, py: 0.5 }} />
          <Tab
            icon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Tirar dúvidas"
            sx={{ minHeight: 40, py: 0.5 }}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", p: 0 }}>
        {mainTab === 1 && (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: { xs: 1.5, sm: 2 } }}>
            {conversationId ? (
              <ConversationTypeAssistant
                conversationId={conversationId}
                variant="dialog"
                showPathInAlert={false}
              />
            ) : (
              <Alert severity="warning">Selecione uma solicitação para tirar dúvidas.</Alert>
            )}
          </Box>
        )}

        {mainTab === 0 && isLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Carregando materiais…</Typography>
          </Box>
        )}

        {mainTab === 0 && isError && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" action={<Button onClick={() => refetch()}>Tentar de novo</Button>}>
              Não foi possível carregar os materiais.
            </Alert>
          </Box>
        )}

        {mainTab === 0 && !isLoading && !isError && levels.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">
              Nenhum material cadastrado para este tipo/subtipo. Use a aba &quot;Tirar dúvidas&quot; para consultar a IA
              com o contexto do atendimento.
            </Alert>
          </Box>
        )}

        {mainTab === 0 && !isLoading && !isError && levels.length > 0 && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: isSmDown ? "column" : "row",
              overflow: "hidden",
            }}
          >
            {!navCollapsed && (
              <Paper
                elevation={0}
                square
                sx={{
                  width: isSmDown ? "100%" : 240,
                  flexShrink: 0,
                  borderRight: isSmDown ? 0 : 1,
                  borderBottom: isSmDown ? 1 : 0,
                  borderColor: "divider",
                  bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "grey.50"),
                  maxHeight: isSmDown ? "36vh" : "none",
                  overflowY: "auto",
                }}
              >
                <Box sx={{ px: 0.75, py: 0.75 }}>
                  <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ fontSize: "0.65rem" }}>
                    Nível (cascata)
                  </Typography>
                  <List dense disablePadding>
                    {levels.map((lvl, idx) => (
                      <ListItemButton
                        key={lvl.typeId}
                        selected={idx === levelIdx}
                        onClick={() => setLevelIdx(idx)}
                        sx={{ borderRadius: 1, mb: 0.25 }}
                      >
                        <ListItemText
                          primary={lvl.label}
                          secondary={`${(lvl.items || []).length} material(is)`}
                          primaryTypographyProps={{ variant: "body2", fontWeight: idx === levelIdx ? 700 : 400 }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItemButton>
                    ))}
                  </List>

                  {selectedLevel && (
                    <>
                      <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ fontSize: "0.65rem", mt: 1, display: "block" }}>
                        Materiais
                      </Typography>
                      <List dense disablePadding>
                        {items.map((it, idx) => (
                          <ListItemButton
                            key={it.id}
                            selected={idx === itemIdx}
                            onClick={() => setItemIdx(idx)}
                            sx={{ borderRadius: 1, mb: 0.25 }}
                          >
                            <ListItemText
                              primary={it.title}
                              secondary={kindLabel(it.kind)}
                              primaryTypographyProps={{
                                variant: "body2",
                                noWrap: true,
                                fontWeight: idx === itemIdx ? 600 : 400,
                              }}
                              secondaryTypographyProps={{ variant: "caption" }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </>
                  )}
                </Box>
              </Paper>
            )}

            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: { xs: 1, sm: 1.5 } }}>
              {selectedItem && (
                <Typography variant="subtitle2" sx={{ mb: 0.75, flexShrink: 0 }} noWrap>
                  {selectedItem.title}
                </Typography>
              )}
              <MaterialViewport item={selectedItem} />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexShrink: 0, px: 1.5, py: 1 }}>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
